import { Component, effect, input, output, signal } from '@angular/core';
import { ContentChange, QuillEditorComponent, QuillModules } from 'ngx-quill';
import Quill from 'quill';

const icons = Quill.import('ui/icons') as Record<string, string>;
icons['myCustomBackButton'] = `
<svg xmlns="http://www.w3.org/2000/svg" width="18" height="24" viewBox="0 0 24 24" fill="none">
<path d="M19.5 12.1878L5.5 12.1878M5.5 12.1878L12.22 5.46777M5.5 12.1878L12.22 18.9078" stroke="#0A3455" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
`;
icons['myCustomPrevButton'] = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <path d="M13.2203 3.68457L6.7793 10.1256L13.2203 16.5666" stroke="#0A3455" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;
icons['myCustomNextButton'] = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
  <path d="M8.5 19L15.5 12L8.5 5" stroke="#0A3455" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

@Component({
  selector: 'app-quill-html-editor',
  standalone: true,
  imports: [QuillEditorComponent],
  template: `
    <quill-editor
      class="quill-editor"
      [modules]="modules"
      [sanitize]="true"
      (onEditorCreated)="onEditorCreated($event)"
      (onContentChanged)="onContentChanged($event)"
    />
  `,
})
export class QuillHtmlEditor {
  readonly templateHtml = input<string>('');

  // Pagination signals
  private currentPage = signal(1);
  readonly totalPages = input<number>(5);

  htmlChangeEvent = output<{ page: number; html: string }>();
  goBackEvent = output<void>();
  getPageHtmlEvent = output<number>();

  private quill = signal<Quill | null>(null);

  protected modules: QuillModules = {
    toolbar: {
      container: [
        ['myCustomBackButton'],
        [
          'bold',
          'italic',
          'underline',
          'strike',
          'blockquote',
          'code-block',
          { list: 'ordered' },
          { list: 'bullet' },
          { indent: '-1' },
          { indent: '+1' },
          { size: ['small', false, 'large', 'huge'] },
          { header: [1, 2, 3, 4, 5, 6, false] },
          { color: [] },
          { background: [] },
          { font: [] },
          { align: [] },
          'link',
          'image',
        ],
        ['myCustomPrevButton', { pageDisplay: true }, 'myCustomNextButton'],
      ],
      handlers: {
        myCustomBackButton: () => {
          this.goBackEvent.emit();
        },
        myCustomPrevButton: () => {
          const current = this.currentPage();
          if (current > 1) this.currentPage.set(current - 1);
          this.getPageHtmlEvent.emit(this.currentPage());
        },
        myCustomNextButton: () => {
          const current = this.currentPage();
          const last = this.totalPages();
          if (current < last) this.currentPage.set(current + 1);
          this.getPageHtmlEvent.emit(this.currentPage());
        },
      },
    },
    clipboard: {
      matchVisual: false,
    },
  };

  constructor() {
    effect(() => {
      const html = this.templateHtml();
      const editor = this.quill();

      this.setContent(editor, html);
      this.createPagination(editor);
    });
  }

  private setContent(editor: Quill | null, html: string): void {
    if (!html || !editor) return;

    editor.setContents([]);
    editor.clipboard.dangerouslyPasteHTML(html);
  }

  private createPagination(editor: Quill | null): void {
    if (!editor) return;

    const toolbar = editor.container.previousElementSibling;
    if (!toolbar) return;

    let pageDisplay = toolbar.querySelector('.ql-pageDisplay') as HTMLElement | null;
    if (!pageDisplay) {
      pageDisplay = document.createElement('span');
      pageDisplay.className = 'ql-pageDisplay';
      const prevButton = toolbar.querySelector('button.ql-myCustomPrevButton');
      const nextButton = toolbar.querySelector('button.ql-myCustomNextButton');
      if (prevButton && nextButton) {
        prevButton.after(pageDisplay);
      }
    }

    pageDisplay.textContent = `${this.currentPage()} of ${this.totalPages()}`;
  }

  protected onEditorCreated(quill: Quill): void {
    this.quill.set(quill);
  }

  protected onContentChanged(event: ContentChange): void {
    this.htmlChangeEvent.emit({ page: this.currentPage(), html: event.html ?? '' });
  }
}
